import { uniqBy } from 'lodash';
import { FormSectionHandler } from '../entity.handler';
import { VendorResponse, VendorUpdateRequest, EmailProviderListResponse, IdNameState } from '@key-telematics/fleet-api-client';
import { AdminEditorService } from '../../admin.service';
import { FormBuilderField, IdNameComboValueHandlers } from 'app/shared/components/form-builder';
import { ComboField, EMPTY_GUID, EntityUtils } from '../utils';
import { nullAccessDenied } from 'app/services';

interface EditorValues {
    emailProvider?: IdNameState;
}

export class VendorNotificationsSectionHandler implements FormSectionHandler<VendorResponse, EditorValues> {

    type = 'form';
    icon = 'at';
    bulk = false;
    readonly = false;

    title = this.translate('TITLE');
    description = this.translate('DESC');

    constructor(
        public admin: AdminEditorService,
        public ownerId: string
    ) {
    }

    private properties = ['emailProvider'];

    translate(key: string, values?: Object): string {
        return this.admin.translate(`EDITORS.VENDOR.NOTIFICATIONS.${key}`, values);
    }

    applyUpdate(entity: VendorResponse, payload: VendorUpdateRequest): Promise<VendorResponse> {
        return this.admin.applyUpdate(api => api.accounts.updateVendorDetails(entity.id, payload));
    }

    mapEntityToEditor(entity: VendorResponse): Promise<EditorValues> {
        const result = EntityUtils.mergeFields<EditorValues>(this.properties, entity, {});
        return Promise.resolve(result);
    }

    mapEditorToEntity(entity: VendorUpdateRequest, values: EditorValues): Promise<any> {
        const result = EntityUtils.mergeFields<EditorValues>(this.properties, entity, values);
        return Promise.resolve(result);
    }

    getFields(entity: VendorResponse): Promise<FormBuilderField[]> {
        return Promise.all([
            this.getEmailProviders(entity),
        ]).then(([emailProviderValues]) => {
            return [
                ComboField('emailProvider', this.translate('FIELDS.EMAILPROVIDER'), true, emailProviderValues, IdNameComboValueHandlers),      
            ];
        }).then(fields => EntityUtils.prepareBulk(!entity, ['emailProvider'], fields));
    }

    async getEmailProviders(vendor: VendorResponse): Promise<{ key: string, value: string }[]> {
        const emailProviders: EmailProviderListResponse = await this.admin.getCached(`listEmailProviders/${vendor.id}/0/100/name/state=active`, api => api.entities.listEmailProviders(vendor.id, 0, 100, 'name', 'state=active')).catch(nullAccessDenied);

        const result = [
            { id: EMPTY_GUID, name: 'Default', state: 'active' },
            ...(vendor?.availableEmailProviders || []),
            ...(emailProviders?.items || []),
        ];

        if (vendor.emailProvider && !result.find(emailProvider => emailProvider.id === vendor.emailProvider.id)) {
            result.unshift({
                id: vendor.emailProvider.id,
                name: vendor.emailProvider.name,
                state: vendor.emailProvider.state === 'active' ? 'unavailable' : vendor.emailProvider.state,
            } as any);
        }

        return uniqBy(result, 'id').map(x => {
            if (x.state && x.state !== 'active') {
                const translationKey = `STATES.${x.state.toUpperCase()}`;
                return { key: x.id, value: `${x.name} (${this.admin.translate(translationKey)})` };
            }

            return { key: x.id, value: x.name };
        }).sort((a, b) => a.value.localeCompare(b.value));
    }

}
